import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Slider from '@react-native-community/slider';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Typography } from '../../theme';
import { useI18n } from '../../i18n';
import {
  canPlayAudioMimeType,
  formatAudioDuration,
  getPlayableVoiceUri,
  pauseVoicePlayback,
  resumeVoicePlayback,
  seekVoicePlayback,
  setVoicePlaybackSpeed,
  startVoicePlayback,
  stopVoicePlayback,
} from '../../services/ChatAudioService';

interface Props {
  audioUrl?: string;
  audioMimeType?: string;
  duration: number;
  waveformData?: number[];
  isMe: boolean;
  status?: 'uploading' | 'sent' | 'failed';
  uploadProgress?: number;
  onRetry?: () => void;
  onCancelUpload?: () => void;
}

const SPEEDS = [1, 1.5, 2];

const VoiceMessageBubble: React.FC<Props> = ({
  audioUrl,
  audioMimeType,
  duration,
  waveformData,
  isMe,
  status = 'sent',
  uploadProgress = 0,
  onRetry,
  onCancelUpload,
}) => {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [resolvedDuration, setResolvedDuration] = useState(duration);
  const [speedIndex, setSpeedIndex] = useState(0);
  const [playbackErrored, setPlaybackErrored] = useState(false);
  const pulse = useRef(new Animated.Value(0)).current;
  const speed = SPEEDS[speedIndex];
  // Checked once per message (its format never changes) — a message this
  // platform/browser genuinely can't decode (e.g. a WebM/Opus note recorded
  // in Chrome, opened on Safari or native) must never be handed to the
  // player at all; see ChatAudioService's canPlayAudioMimeType for why.
  const canPlay = useMemo(() => canPlayAudioMimeType(audioMimeType), [audioMimeType]);

  useEffect(() => {
    if (!playing) {
      pulse.stopAnimation();
      pulse.setValue(0);
      return undefined;
    }
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 420, useNativeDriver: false }),
        Animated.timing(pulse, { toValue: 0, duration: 420, useNativeDriver: false }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [playing, pulse]);

  useEffect(() => () => {
    if (playing) stopVoicePlayback().catch(() => undefined);
  }, [playing]);

  const waveform = useMemo(() => {
    const fallback = [0.26, 0.48, 0.74, 0.38, 0.86, 0.52, 0.7, 0.32, 0.6, 0.42, 0.76, 0.3, 0.5, 0.82, 0.34, 0.58, 0.46, 0.68, 0.4, 0.28];
    return waveformData?.length ? waveformData : fallback;
  }, [waveformData]);

  const progress = resolvedDuration > 0 ? Math.max(0, Math.min(1, position / resolvedDuration)) : 0;

  const togglePlayback = useCallback(async () => {
    if (!audioUrl || status !== 'sent' || !canPlay) return;
    try {
      if (!playing && position <= 0) {
        const playableUri = await getPlayableVoiceUri(audioUrl);
        setPlaying(true);
        await startVoicePlayback(
          playableUri,
          speed,
          update => {
            setPosition(update.positionMs);
            if (update.durationMs > 0) setResolvedDuration(update.durationMs);
          },
          () => {
            setPlaying(false);
            setPosition(0);
            stopVoicePlayback().catch(() => undefined);
          },
        );
      } else if (playing) {
        await pauseVoicePlayback();
        setPlaying(false);
      } else {
        await resumeVoicePlayback(speed);
        setPlaying(true);
      }
    } catch (error) {
      console.warn('[Tonyte][RN][Voice] playback failed', error);
      setPlaying(false);
      setPlaybackErrored(true);
    }
  }, [audioUrl, canPlay, playing, position, speed, status]);

  const cycleSpeed = useCallback(async () => {
    const nextIndex = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(nextIndex);
    if (playing) {
      await setVoicePlaybackSpeed(SPEEDS[nextIndex]);
    }
  }, [playing, speedIndex]);

  const handleSeek = useCallback(async (value: number) => {
    const next = value * resolvedDuration;
    setPosition(next);
    if (audioUrl && status === 'sent') {
      await seekVoicePlayback(next);
    }
  }, [audioUrl, resolvedDuration, status]);

  const animatedOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.64, 1] });
  const cannotPlayOnThisDevice = status === 'sent' && (!canPlay || playbackErrored);
  const statusLabel = status === 'uploading'
    ? `Uploading ${Math.round(uploadProgress * 100)}%`
    : status === 'failed'
      ? 'Upload failed'
      : cannotPlayOnThisDevice
        ? t('chat.voiceCannotPlayDevice', "Can't play on this device")
        : formatAudioDuration(resolvedDuration || duration);

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        style={[styles.playButton, isMe ? styles.playButtonMe : styles.playButtonThem]}
        activeOpacity={0.84}
        onPress={() => void togglePlayback()}
        disabled={status !== 'sent' || cannotPlayOnThisDevice}
      >
        <Icon
          name={cannotPlayOnThisDevice ? 'slash' : playing ? 'pause' : status === 'failed' ? 'alert-circle' : 'play'}
          size={14}
          color="#fff"
        />
      </TouchableOpacity>

      <View style={styles.body}>
        <View style={styles.waveform}>
          {waveform.map((value, index) => {
            const isPast = index / Math.max(1, waveform.length - 1) <= progress;
            return (
              <Animated.View
                key={`${index}-${value}`}
                style={[
                  styles.bar,
                  {
                    height: 6 + value * 22,
                    opacity: isPast ? animatedOpacity : 0.38,
                    backgroundColor: isMe ? '#fff' : 'rgba(255,255,255,0.86)',
                  },
                ]}
              />
            );
          })}
        </View>
        <Slider
          style={styles.slider}
          value={progress}
          minimumValue={0}
          maximumValue={1}
          minimumTrackTintColor={isMe ? '#fff' : Colors.sunset}
          maximumTrackTintColor="rgba(255,255,255,0.18)"
          thumbTintColor={isMe ? '#fff' : Colors.sunset}
          disabled={status !== 'sent' || cannotPlayOnThisDevice}
          onSlidingComplete={value => void handleSeek(value)}
        />
        <View style={styles.footer}>
          <Text style={styles.duration}>{statusLabel}</Text>
          {status === 'failed' ? (
            <View style={styles.actionRow}>
              <TouchableOpacity onPress={onRetry} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={styles.actionText}>{t('common.retry')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={onCancelUpload} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Text style={styles.actionText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity onPress={() => void cycleSpeed()} disabled={status !== 'sent' || cannotPlayOnThisDevice} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Text style={styles.speed}>{speed}x</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {status === 'uploading' && onCancelUpload ? (
        <TouchableOpacity style={styles.cancelUpload} onPress={onCancelUpload} activeOpacity={0.82}>
          <Icon name="x" size={13} color="rgba(255,255,255,0.72)" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

export default VoiceMessageBubble;

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    minWidth: 228,
    maxWidth: 260,
  },
  playButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButtonMe: { backgroundColor: 'rgba(255,255,255,0.24)' },
  playButtonThem: { backgroundColor: 'rgba(242,125,38,0.86)' },
  body: { flex: 1, minWidth: 0 },
  waveform: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 30,
  },
  bar: {
    flex: 1,
    borderRadius: 2,
  },
  slider: {
    height: 16,
    marginHorizontal: -12,
    marginTop: -2,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  duration: { color: 'rgba(255,255,255,0.72)', fontSize: Typography.xs, fontWeight: Typography.semibold },
  speed: {
    color: Colors.textPrimary,
    fontSize: 10,
    fontWeight: Typography.extrabold,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  actionRow: { flexDirection: 'row', gap: 8 },
  actionText: { color: Colors.sunset, fontSize: 10, fontWeight: Typography.extrabold },
  cancelUpload: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
});
